<template>
  <b-modal
    id="modal-regularization"
    ref="modal-regularization"
    title="Regularize Attendance"
    ok-title="Submit"
    cancel-variant="outline-secondary"
    @ok="handleSubmit"
    @hidden="handleHidden"
    size="md"
  >
    <b-form v-if="selectedRegularizationItem">
      <b-alert variant="info" show class="mb-2">
        <strong>Issue:</strong> {{ selectedRegularizationItem.issue_type }}<br>
        <strong>Date:</strong> {{ formatDate(selectedRegularizationItem.datestring) }}
      </b-alert>
      
      <!-- Show Existing Data Section -->
      <div v-if="selectedRegularizationItem.punchData || selectedRegularizationItem.biometric || selectedRegularizationItem.intime || selectedRegularizationItem.outtime || selectedRegularizationItem.biometricRecord" class="mb-3">
        <b-alert variant="light" show class="mb-2">
          <strong class="d-block mb-2">Existing Data:</strong>
          
          <!-- Punch Data -->
          <div v-if="selectedRegularizationItem.punchData" class="mb-2">
            <strong>Punch Data:</strong>
            <div class="ml-3">
              <div v-if="selectedRegularizationItem.punchData.intime">
                <small>IN Time: <strong>{{ selectedRegularizationItem.punchData.intime }}</strong></small>
              </div>
              <div v-if="selectedRegularizationItem.punchData.outtime">
                <small>OUT Time: <strong>{{ selectedRegularizationItem.punchData.outtime }}</strong></small>
              </div>
              <div v-if="selectedRegularizationItem.punchData.worktime">
                <small>Work Time: <strong>{{ selectedRegularizationItem.punchData.worktime }}</strong></small>
              </div>
            </div>
          </div>
          
          <!-- Biometric Data -->
          <div v-if="(selectedRegularizationItem.biometric || selectedRegularizationItem.biometricRecord) && !isWFHLeaveType(selectedRegularizationItem.leave_type)" class="mb-2">
            <strong>Biometric Data:</strong>
            <div class="ml-3">
              <div v-if="(selectedRegularizationItem.biometric && selectedRegularizationItem.biometric.intime) || (selectedRegularizationItem.biometricRecord && selectedRegularizationItem.biometricRecord.intime)">
                <small>IN Time: <strong>{{ getBiometricIntime(selectedRegularizationItem) }}</strong></small>
              </div>
              <div v-if="(selectedRegularizationItem.biometric && selectedRegularizationItem.biometric.outtime) || (selectedRegularizationItem.biometricRecord && selectedRegularizationItem.biometricRecord.outtime)">
                <small>OUT Time: <strong>{{ getBiometricOuttime(selectedRegularizationItem) }}</strong></small>
              </div>
              <div v-if="(selectedRegularizationItem.biometric && selectedRegularizationItem.biometric.worktime) || (selectedRegularizationItem.biometricRecord && selectedRegularizationItem.biometricRecord.worktime)">
                <small>Work Time: <strong>{{ getBiometricWorktime(selectedRegularizationItem) }}</strong></small>
              </div>
            </div>
          </div>
          
          <!-- WFH Note: Biometric Not Required -->
          <div v-if="isWFHLeaveType(selectedRegularizationItem.leave_type)" class="mb-2">
            <small class="text-muted">
              <feather-icon icon="InfoIcon" size="14" class="mr-1" />
              <em>Biometric data not required for Work From Home</em>
            </small>
          </div>
          
          <!-- Show available times if no structured data but times exist -->
          <div v-if="!selectedRegularizationItem.punchData && !selectedRegularizationItem.biometric && !selectedRegularizationItem.biometricRecord && (selectedRegularizationItem.intime || selectedRegularizationItem.outtime)" class="mb-2">
            <strong>Available Times:</strong>
            <div class="ml-3">
              <div v-if="selectedRegularizationItem.intime && selectedRegularizationItem.intime !== '0:00:00'">
                <small>IN Time: <strong>{{ selectedRegularizationItem.intime }}</strong></small>
              </div>
              <div v-if="selectedRegularizationItem.outtime && selectedRegularizationItem.outtime !== '0:00:00'">
                <small>OUT Time: <strong>{{ selectedRegularizationItem.outtime }}</strong></small>
              </div>
              <div v-if="selectedRegularizationItem.worktime">
                <small>Work Time: <strong>{{ selectedRegularizationItem.worktime }}</strong></small>
              </div>
            </div>
          </div>
          
          <!-- Show message if no data available -->
          <div v-if="!selectedRegularizationItem.punchData && !selectedRegularizationItem.biometric && !selectedRegularizationItem.biometricRecord && !selectedRegularizationItem.intime && !selectedRegularizationItem.outtime" class="text-muted">
            <small>No existing punch or biometric data available</small>
          </div>
        </b-alert>
      </div>
      
      <b-row>
        <b-col 
          v-if="showInTimeField" 
          :cols="showOutTimeField ? 12 : 12" 
          :sm="showOutTimeField ? 6 : 12" 
          :class="showOutTimeField ? 'mb-2 mb-sm-0' : ''"
        >
          <b-form-group label="In Time" label-for="reg-intime">
            <b-form-input
              id="reg-intime"
              v-model="regularizationForm.intime"
              type="time"
              :required="showInTimeField && requiresRegularizationTime(regularizationForm.leave_type) && (isPresentOrHalfDayLeaveType(regularizationForm.leave_type) || (!regularizationForm.leave_type && (!selectedRegularizationItem.intime || selectedRegularizationItem.intime === '0:00:00' || selectedRegularizationItem.intime.trim() === '')))"
            />
          </b-form-group>
        </b-col>
        <b-col 
          v-if="showOutTimeField" 
          :cols="showInTimeField ? 12 : 12" 
          :sm="showInTimeField ? 6 : 12"
        >
          <b-form-group label="Out Time" label-for="reg-outtime">
            <b-form-input
              id="reg-outtime"
              v-model="regularizationForm.outtime"
              type="time"
              :required="showOutTimeField && requiresRegularizationTime(regularizationForm.leave_type) && (isPresentOrHalfDayLeaveType(regularizationForm.leave_type) || (!regularizationForm.leave_type && (!selectedRegularizationItem.outtime || selectedRegularizationItem.outtime === '0:00:00' || selectedRegularizationItem.outtime.trim() === '')))"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-form-group 
        label="Leave Type (Optional)" 
        label-for="reg-leave-type"
        class="mb-2"
      >
        <b-form-select
          id="reg-leave-type"
          v-model="regularizationForm.leave_type"
          :options="[{ value: null, text: 'Select leave type (optional)' }, ...leaveTypeOptions]"
          placeholder="Select leave type (optional)"
        />
        <small class="text-muted">Present and half-day require times. Other leave types (Sick, Casual, WFH, etc.) do not require times.</small>
      </b-form-group>
      <b-form-group label-for="reg-comment">
        <template #label>
          Reason/Comment <span class="text-danger">*</span>
        </template>
        <b-form-textarea
          id="reg-comment"
          v-model="regularizationForm.comment"
          rows="3"
          placeholder="Enter reason for regularization"
          :state="commentError ? false : null"
          @input="commentError = null"
        />
        <small v-if="commentError" class="text-danger">{{ commentError }}</small>
      </b-form-group>
      <b-alert variant="warning" show>
        <small>This will require manager and HR approval.</small>
      </b-alert>
    </b-form>
  </b-modal>
</template>

<script>
import {
  BModal,
  BForm,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormTextarea,
  BAlert,
  BRow,
  BCol,
} from 'bootstrap-vue'
import {
  isPresentOrHalfDayLeaveType,
  isWFHLeaveType,
  requiresRegularizationTime,
} from '@/utils/attendance-leave-types'

export default {
  name: 'RegularizationModal',
  components: {
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormTextarea,
    BAlert,
    BRow,
    BCol,
  },
  props: {
    regularizationForm: {
      type: Object,
      required: true,
    },
    selectedRegularizationItem: {
      type: Object,
      default: null,
    },
    leaveTypeOptions: {
      type: Array,
      default: () => [],
    },
    showInTimeField: {
      type: Boolean,
      default: true,
    },
    showOutTimeField: {
      type: Boolean,
      default: true,
    },
    formatDate: {
      type: Function,
      required: true,
    },
    getBiometricIntime: {
      type: Function,
      required: true,
    },
    getBiometricOuttime: {
      type: Function,
      required: true,
    },
    getBiometricWorktime: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      /** Set only on a failed submit attempt, cleared as soon as the user edits the field - same "guard right before emit" pattern as `TaskFormModal.vue`'s required-field checks. */
      commentError: null,
    }
  },
  methods: {
    handleSubmit(bvModalEvt) {
      this.commentError = null

      if (!this.regularizationForm.comment || !this.regularizationForm.comment.trim()) {
        this.commentError = 'Reason/Comment is required.'
        // Block both the modal auto-close and the emit to the parent, so
        // `handleRegularize` (and therefore the `addPost` API call) never
        // runs for an invalid submission.
        bvModalEvt.preventDefault()
        return
      }

      this.$emit('submit', bvModalEvt)
    },
    handleHidden() {
      this.commentError = null
      this.$emit('reset')
    },
    show() {
      this.$refs['modal-regularization'].show()
    },
    hide() {
      this.$refs['modal-regularization'].hide()
    },
    isPresentOrHalfDayLeaveType,
    isWFHLeaveType,
    requiresRegularizationTime,
  },
}
</script>

<style scoped>
/* Scoped styles if any specific to this modal */
</style>